import React from 'react';

import { Text } from '@opendoor/bricks/core';

import { FAQs, FAQItem } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/FAQs';

const EXTERIOR_ASSESSMENT_FAQ_ITEMS: Array<FAQItem> = [
  {
    question: 'What are you looking for during the assessment?',
    answer: (
      <Text mb={3}>
        We're reviewing the exterior of the home to ensure that major areas are safe, functional,
        and in good repair.
      </Text>
    ),
  },
  {
    question: 'Do I need to be home for this?',
    answer: (
      <Text mb={3}>
        You don't need to be home for the exterior assessment as long as you provide access to the
        backyard. We love pets, but please make sure they're put away.
      </Text>
    ),
  },
  {
    question: 'What precautions are you taking due to COVID-19?',
    answer: (
      <Text mb={3}>
        The Opendoor team member who visits your home will wear a face mask and practice safe social
        distancing.
      </Text>
    ),
  },
  {
    question: 'Do you need to come inside the home?',
    answer: (
      <Text mb={3}>
        We don't need to come inside the home for the exterior assessment. This is because you
        already showed us the interior of your home. Thank you for giving us a tour!
      </Text>
    ),
  },
  {
    question: 'When will I get my final offer?',
    answer: (
      <Text mb={3}>
        Once we're done with the exterior assessment it typically takes a few days to email your
        final offer. You'll be able to see your final offer price along with the service charge, any
        repair cost, and your estimated closing costs.
      </Text>
    ),
  },
];

const AGENT_EXTERIOR_ASSESSMENT_FAQ_ITEMS: Array<FAQItem> = [
  {
    question: 'What are you looking for during the assessment?',
    answer: (
      <Text mb={3}>
        We’re reviewing the exterior of the home to ensure that major areas are safe, functional,
        and in good repair.
      </Text>
    ),
  },
  {
    question: 'Does my client need to be home for this?',
    answer: (
      <Text mb={3}>
        No one needs to be home for the exterior assessment as long as your client provides access
        to the backyard. We love pets, but please make sure they’re put away.
      </Text>
    ),
  },
  {
    question: 'What precautions are you taking due to COVID-19?',
    answer: (
      <Text mb={3}>
        The Opendoor team member who visits your home will wear a face mask and practice safe social
        distancing.
      </Text>
    ),
  },
  {
    question: 'Do you need to come inside the home?',
    answer: (
      <Text mb={3}>
        We don’t need to come inside the home for the exterior assessment. This is because you
        already showed us the interior of your client’s home or submitted the seller’s disclosure.
      </Text>
    ),
  },
  {
    question: "When will I get my client's final offer?",
    answer: (
      <Text mb={3}>
        Once we’re done with the exterior assessment it typically takes a few days to complete your
        client’s final offer. You’ll be able to see your client’s final offer price along with the
        service charge, any repair cost, and the estimated closing costs.
      </Text>
    ),
  },
];

const getInPersonExteriorAssessmentFaqItems = (epContactPhoneOrEmail: string): Array<FAQItem> => [
  {
    question: 'What are you looking for during the assessment?',
    answer: (
      <Text mb={3}>
        We’re reviewing the home to ensure that major areas are safe, functional, and in good
        repair.
      </Text>
    ),
  },
  {
    question: 'What precautions are you taking due to COVID-19?',
    answer: (
      <Text mb={3}>
        The Opendoor team member who visits your home will bring a face mask and practice safe
        social distancing.
      </Text>
    ),
  },
  {
    question: 'Do I need to be home for this?',
    answer: (
      <Text mb={3}>
        You or someone over the age of 18 will need to be home. If you can provide another way for
        us to access the home, reach out to your Opendoor Expert at {epContactPhoneOrEmail}.
      </Text>
    ),
  },
  {
    question: 'When will I get my final offer?',
    answer: (
      <Text mb={3}>
        Once we’re done with the home assessment, it typically takes a few days to email your final
        offer. You’ll be able to see your final offer price along with the service charge, any
        repair cost, and your estimated closing costs.
      </Text>
    ),
  },
];

const AGENT_IN_PERSON_EXTERIOR_ASSESSMENT_FAQ_ITEMS: Array<FAQItem> = [
  {
    question: 'What are you looking for during the assessment?',
    answer: (
      <Text mb={3}>
        We’re reviewing the home to ensure that major areas are safe, functional, and in good
        repair.
      </Text>
    ),
  },
  {
    question: 'Does anyone need to be at the home for this?',
    answer: (
      <Text mb={3}>
        Since we need access to the backyard and interior of the home, either you, your seller, or
        someone over 18 will need to provide access. If no one can be home, please contact your
        Opendoor expert to provide access instructions.
      </Text>
    ),
  },
  {
    question: 'What precautions are you taking due to COVID-19?',
    answer: (
      <Text mb={3}>
        The Opendoor team member who visits your home will bring a face mask and practice safe
        social distancing.
      </Text>
    ),
  },

  {
    question: 'When will I get my client’s final offer?',
    answer: (
      <Text mb={3}>
        Once we’re done with the home assessment, it typically takes a few days to complete your
        client’s final offer. You’ll be able to see your client’s final offer price along with the
        service charge, any repair cost, and the estimated closing costs.
      </Text>
    ),
  },
];

/** TODO: Add documentation */
export const ExteriorAssessmentFAQs: React.FC<{
  shouldSeeAgentExperience?: boolean;
  isInPersonSseaEnabled?: boolean;
  epContactPhoneOrEmail: string;
}> = ({ isInPersonSseaEnabled, shouldSeeAgentExperience, epContactPhoneOrEmail }) => {
  const inPersonExteriorAssessmentFAQItems = shouldSeeAgentExperience
    ? AGENT_IN_PERSON_EXTERIOR_ASSESSMENT_FAQ_ITEMS
    : getInPersonExteriorAssessmentFaqItems(epContactPhoneOrEmail);

  const exteriorAssessmentFAQItems = shouldSeeAgentExperience
    ? AGENT_EXTERIOR_ASSESSMENT_FAQ_ITEMS
    : EXTERIOR_ASSESSMENT_FAQ_ITEMS;

  return (
    <FAQs
      items={
        isInPersonSseaEnabled ? inPersonExteriorAssessmentFAQItems : exteriorAssessmentFAQItems
      }
    />
  );
};
